// Platform Firms — Charpen-only cross-firm admin page (Phase 4-κ).
// Only platform_operator sees this in the sidebar and only they can
// call the backing /api/admin/firms endpoints. Everyone else 403s.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

export function PlatformFirmsPage({ authStatus, showNotification }) {
  const [firms, setFirms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    slug: "", display_name: "", short_name: "",
    primary_email: "", primary_phone: "",
    letterhead_name: "", letterhead_address: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch("/api/admin/firms");
      setFirms(d.data || []);
    } catch (e) {
      if (e.status === 403) {
        showNotification?.("Only Platform Operators can access this page.", "warning");
      } else {
        showNotification?.(`Failed to load firms: ${e.message}`, "error");
      }
      setFirms([]);
    }
    setLoading(false);
  }, [showNotification]);

  useEffect(() => { load(); }, [load]);

  const update = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const create = async () => {
    if (creating) return;
    if (!form.slug.trim() || !form.display_name.trim() || !form.short_name.trim()) {
      showNotification?.("Slug, display name, and short name are required.", "warning");
      return;
    }
    setCreating(true);
    try {
      await apiFetch("/api/admin/firms", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setForm({
        slug: "", display_name: "", short_name: "",
        primary_email: "", primary_phone: "",
        letterhead_name: "", letterhead_address: "",
      });
      setShowCreate(false);
      showNotification?.(`Firm "${form.display_name}" created.`, "success");
      await load();
    } catch (e) {
      if (e.status === 409) {
        showNotification?.(e.message, "warning");
      } else {
        showNotification?.(`Create failed: ${e.message}`, "error");
      }
    }
    setCreating(false);
  };

  const toggleActive = async (firm) => {
    const next = !firm.is_active;
    if (!window.confirm(`${next ? "Reactivate" : "Deactivate"} firm "${firm.display_name}"?\n\nDeactivating disables all users on this firm and hides it from subdomain resolution.`)) return;
    try {
      await apiFetch(`/api/admin/firms/${firm.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: next }),
      });
      showNotification?.(`Firm ${next ? "reactivated" : "deactivated"}.`, "success");
      await load();
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
  };

  if (loading) {
    return React.createElement("div", { style: { padding: "40px", color: C.textMuted } },
      "Loading platform firms…"
    );
  }

  const totalUsers = firms.reduce((n, f) => n + (f.stats?.firm_users || 0), 0);
  const totalTasks = firms.reduce((n, f) => n + (f.stats?.tasks || 0), 0);

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1200px" } },
    // Header
    React.createElement("div", {
      style: { marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }
    },
      React.createElement("div", null,
        React.createElement("h1", {
          style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" }
        }, "Platform Firms"),
        React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
          `${firms.length} firm${firms.length === 1 ? "" : "s"} on the platform · ${totalUsers} total users · ${totalTasks} total tasks`
        ),
      ),
      React.createElement("button", {
        onClick: () => setShowCreate(v => !v),
        style: {
          padding: "10px 20px", borderRadius: "8px", border: "none",
          background: C.gold, color: "#fff", fontSize: "14px", fontWeight: 600,
          cursor: "pointer",
        }
      }, showCreate ? "Cancel" : "+ New firm"),
    ),

    // Create form (conditional)
    showCreate && React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "24px",
        border: `1px solid ${C.warmGray}`, marginBottom: "20px",
      }
    },
      React.createElement("h2", {
        style: {
          fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
          letterSpacing: "0.08em", fontWeight: 600, margin: "0 0 16px 0",
        }
      }, "Create new firm"),

      _field("Slug (URL identifier)", "Lowercase, URL-safe. Used for subdomain resolution (e.g. 'acmelegal' → acmelegal.shinglelaw.com). Cannot change later.",
        _textInput(form.slug, update("slug"), 50, "e.g. acmelegal"),
      ),
      _field("Display name", "Full firm name shown in headers, emails, and letterhead.",
        _textInput(form.display_name, update("display_name"), 200, "e.g. Acme & Partners, LLC"),
      ),
      _field("Short name / monogram", "Sidebar monogram, email display name suffix.",
        _textInput(form.short_name, update("short_name"), 50, "e.g. Acme"),
      ),
      _field("Primary email (optional)", "Public contact address. Can be set later.",
        _textInput(form.primary_email, update("primary_email"), 200, "contact@acmelegal.com"),
      ),
      _field("Primary phone (optional)", null,
        _textInput(form.primary_phone, update("primary_phone"), 50, "(555) 123-4567"),
      ),
      _field("Letterhead name (optional)", "How the firm name appears on generated letters. Defaults to display name.",
        _textInput(form.letterhead_name, update("letterhead_name"), 200, ""),
      ),
      _field("Letterhead address (optional)", "Full mailing address. Multi-line.",
        _textArea(form.letterhead_address, update("letterhead_address"), 3, ""),
      ),

      React.createElement("div", { style: { display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "16px" } },
        React.createElement("button", {
          onClick: () => setShowCreate(false),
          style: {
            padding: "8px 16px", border: `1px solid ${C.warmGray}`,
            background: "#fff", color: C.textMuted, borderRadius: "8px",
            fontSize: "13px", fontWeight: 600, cursor: "pointer",
          }
        }, "Cancel"),
        React.createElement("button", {
          onClick: create,
          disabled: creating,
          style: {
            padding: "8px 20px", borderRadius: "8px", border: "none",
            background: creating ? C.warmGray : C.gold,
            color: "#fff", fontSize: "13px", fontWeight: 600,
            cursor: creating ? "default" : "pointer",
          }
        }, creating ? "Creating…" : "Create firm"),
      ),
    ),

    // Firm list
    firms.length === 0
      ? React.createElement("div", {
          style: {
            padding: "40px", textAlign: "center", color: C.textMuted,
            background: "#fff", borderRadius: "12px", border: `1px solid ${C.warmGray}`,
          }
        }, "No firms on the platform yet.")
      : React.createElement("div", {
          style: { background: "#fff", borderRadius: "12px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
        },
          React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
            React.createElement("thead", null,
              React.createElement("tr", {
                style: { background: C.warmWhite, borderBottom: `1px solid ${C.warmGray}` }
              },
                _th("Firm"),
                _th("Slug"),
                _th("Users"),
                _th("Tasks"),
                _th("Portal clients"),
                _th("Templates"),
                _th("Status"),
                _th(""),
              ),
            ),
            React.createElement("tbody", null,
              ...firms.map(f => React.createElement("tr", {
                key: f.id,
                style: { borderBottom: `1px solid ${C.warmGray}` }
              },
                _td(React.createElement("div", null,
                  React.createElement("div", { style: { fontWeight: 600, color: C.navy } }, f.display_name),
                  React.createElement("div", { style: { fontSize: "11px", color: C.textMuted } }, `id ${f.id} · ${f.short_name}`),
                )),
                _td(React.createElement("code", {
                  style: { fontSize: "12px", background: C.warmWhite, padding: "2px 6px", borderRadius: "4px" }
                }, f.slug)),
                _td(f.stats?.firm_users ?? 0),
                _td(f.stats?.tasks ?? 0),
                _td(f.stats?.portal_clients ?? 0),
                _td(f.stats?.matter_templates ?? 0),
                _td(React.createElement("span", {
                  style: {
                    padding: "3px 10px", borderRadius: "999px", fontSize: "11px",
                    fontWeight: 600,
                    background: f.is_active ? "#DCFCE7" : "#FEE2E2",
                    color: f.is_active ? "#166534" : "#991B1B",
                  }
                }, f.is_active ? "Active" : "Inactive")),
                _td(React.createElement("button", {
                  onClick: () => toggleActive(f),
                  style: {
                    padding: "4px 10px", borderRadius: "6px",
                    border: `1px solid ${C.warmGray}`, background: "#fff",
                    color: C.textMuted, fontSize: "11px", cursor: "pointer",
                  }
                }, f.is_active ? "Deactivate" : "Reactivate")),
              )),
            ),
          ),
        ),
  );
}

// ─── Small helpers ─────────────────────────────────────────

function _field(label, hint, control) {
  return React.createElement("div", { style: { marginBottom: "12px" } },
    React.createElement("label", {
      style: { display: "block", fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "4px" }
    }, label),
    hint && React.createElement("div", {
      style: { fontSize: "12px", color: C.textMuted, marginBottom: "6px" }
    }, hint),
    control,
  );
}

function _textInput(value, onChange, maxLength, placeholder) {
  return React.createElement("input", {
    type: "text", value, onChange, maxLength,
    placeholder: placeholder || "",
    style: {
      width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
    }
  });
}

function _textArea(value, onChange, rows, placeholder) {
  return React.createElement("textarea", {
    value, onChange, rows,
    placeholder: placeholder || "",
    style: {
      width: "100%", padding: "8px 10px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
      fontFamily: "inherit", resize: "vertical",
    }
  });
}

function _th(text) {
  return React.createElement("th", {
    style: {
      textAlign: "left", padding: "10px 14px", fontSize: "11px",
      color: C.textMuted, textTransform: "uppercase",
      letterSpacing: "0.06em", fontWeight: 600,
    }
  }, text);
}

function _td(content) {
  return React.createElement("td", {
    style: { padding: "12px 14px", fontSize: "13px", color: C.textDark }
  }, content);
}
